import React, { useEffect, useState } from 'react';
import {
    Clock,
    Users,
    Search,
    Filter,
    Edit3,
    Trash2,
    ArrowUpDown,
    Play,
    Square,
    Timer,
    Calendar,
    User as UserIcon,
    Wrench,
    X,
} from 'lucide-react';

import axios from 'helpers/axios';
import config from 'helpers/config';
import User from 'models/UserModel';
import UserClockInCard from './UserClockInCard';
import ClockIn from 'models/ClockIn';
import moment from 'moment';
import dateFormats from 'helpers/dateFormats';
import TableColumn from 'models/TableColumnModel';
import RepairOrder from 'models/RepairOrderModel';
import TimeLogModal from './TimeLogModal';
import { displayConfirmationModal } from 'helpers/services/removeConfirmationService';
import Overlay from 'components/common/Overlay';
import { connect } from 'react-redux';

interface Props {
    currentUser: User | null;
}

const TimeLogsPage: React.FC<Props> = ({ currentUser }) => {
    const [users, setUsers] = useState<User[]>([]);
    const [timeLogs, setTimeLogs] = useState<ClockIn[]>([]);
    const [activeRepairOrders, setActiveRepairOrders] = useState<RepairOrder[]>([]);
    const [isEditTimeLogModelOpen, setIsEditTimeLogModelOpen] = useState<boolean>(false);
    const [editableTimeLog, setEditableTimeLog] = useState<ClockIn | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [selectedUserId, setSelectedUserId] = useState<string>('All Employees');
    const [selectedStatus, setSelectedStatus] = useState<string>('All Status');

    useEffect(() => {
        fetchTimeLogs();
        fetchActiveROS();
    }, []);

    useEffect(() => {
        if (currentUser) {
            fetchUsers();
        }
    }, [currentUser]);

    const fetchUsers = async () => {
        axios
            .get(`${config.apiUrl}/user`, {
                params: {
                    withTimeLogs: true,
                },
            })
            .then((response) => {
                if (currentUser) {
                    // first show current user, then order all other users by name
                    const loggedInUser = response.data.find((user: User) => user.id === currentUser.id);
                    const otherUsers = response.data.filter((user: User) => user.id !== currentUser.id);

                    // Only include loggedInUser if it exists, otherwise just show sorted users
                    const sortedUsers = loggedInUser
                        ? [
                              loggedInUser,
                              ...otherUsers.sort((a: User, b: User) => a.firstName.localeCompare(b.firstName)),
                          ]
                        : otherUsers.sort((a: User, b: User) => a.firstName.localeCompare(b.firstName));

                    setUsers(sortedUsers);
                } else {
                    // Fallback: just sort all users by name if currentUser is not available
                    setUsers(response.data.sort((a: User, b: User) => a.firstName.localeCompare(b.firstName)));
                }
            })
            .catch((err) => console.log(err));
    };

    const fetchTimeLogs = async (userId?: string, status?: string) => {
        const params: any = {};

        // Add userId filter if specified and not "All Employees"
        if (userId && userId !== 'All Employees') {
            params.userId = userId;
        }

        // Add status filter if specified and not "All Status"
        if (status && status !== 'All Status') {
            params[status] = true;
        }

        axios
            .get(`${config.apiUrl}/timeLog/`, { params })
            .then((response) => {
                setTimeLogs(response.data);
                setIsLoading(false);
            })
            .catch((err) => console.log(err));
    };

    const handleFilterChange = (userId: string, status: string) => {
        setSelectedUserId(userId);
        setSelectedStatus(status);
        fetchTimeLogs(userId, status);
    };

    const fetchActiveROS = () => {
        axios
            .get(`${config.apiUrl}/repairOrder/board`)
            .then((res) => setActiveRepairOrders(res.data))
            .catch((_) => console.log(_));
    };

    const isColumnActive = (columnName: string) => {
        return timeLogColumns.find((column) => column.name === columnName)?.active;
    };

    const getDuration = (timeLog: ClockIn) => {
        const durationMilliseconds = moment
            .duration(moment(timeLog.clockOutTime).diff(moment(timeLog.clockInTime)))
            .as('milliseconds');
        const durationDays = Math.floor(durationMilliseconds / (1000 * 60 * 60 * 24));
        const remainingMilliseconds = durationMilliseconds % (1000 * 60 * 60 * 24);

        return `${durationDays > 0 ? `${durationDays} day${durationDays > 1 ? 's' : ''}, ` : ''}${moment
            .utc(remainingMilliseconds)
            .format('HH:mm:ss')}`;
    };

    const handleDelete = (timeLog: ClockIn) => {
        displayConfirmationModal()
            .then(() => {
                axios
                    .delete(`${config.apiUrl}/timeLog/${timeLog.id}`)
                    .then((_) => {
                        fetchTimeLogs(selectedUserId, selectedStatus);
                    })
                    .catch((err) => console.log(err));
            })
            .catch((_) => _);
    };

    const handleEdit = (timeLog: ClockIn) => {
        setIsEditTimeLogModelOpen(true);
        setEditableTimeLog(timeLog);
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50 pb-96">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-8 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
                            <Timer className="h-8 w-8 text-blue-600" />
                            <span>Time Logs</span>
                        </h2>
                        <p className="text-gray-500 mt-1">
                            Track employee time and manage work sessions across repair orders.
                        </p>
                    </div>
                </div>
            </div>
            <div className="flex-1 p-8">
                {/* Employee Clock-in Cards */}
                <div className="mb-8">
                    <div className="flex items-center space-x-2 mb-4">
                        <UserIcon className="h-5 w-5 text-gray-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Employee's Clocks</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {users.map((user) => (
                            <UserClockInCard
                                key={user.id}
                                user={user}
                                timeLogs={timeLogs.filter((log) => log.userId === user.id)}
                                onChange={() => fetchTimeLogs(selectedUserId, selectedStatus)}
                                activeRepairOrders={activeRepairOrders}
                            />
                        ))}
                    </div>
                </div>

                {/* Time Logs Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                                    <Clock className="h-5 w-5" />
                                    <span>Time Log History</span>
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    Complete history of all time tracking sessions
                                </p>
                            </div>
                            <div className="flex items-center space-x-3">
                                {/* <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                    <input
                                        type="text"
                                        placeholder="Search time logs..."
                                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    />
                                </div> */}
                                <button
                                    className="flex items-center text-black space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    onClick={() => {
                                        setShowFilters(!showFilters);
                                    }}
                                >
                                    <Filter className="h-4 w-4" />
                                    <span className="text-sm">Filters</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {isLoading && <Overlay />}

                    {showFilters && (
                        <div className="p-4 bg-gray-50 border-b border-gray-200">
                            <div className="flex gap-4 items-center">
                                <select
                                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    value={selectedUserId}
                                    onChange={(e) => {
                                        const userId = e.target.value;
                                        handleFilterChange(userId, selectedStatus);
                                    }}
                                >
                                    <option>All Employees</option>
                                    {users.map((user) => (
                                        <option key={user.id} value={user.id}>
                                            {user.firstName} {user.lastName}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    value={selectedStatus}
                                    onChange={(e) => {
                                        const timeLogStatus = e.target.value;
                                        handleFilterChange(selectedUserId, timeLogStatus);
                                    }}
                                >
                                    <option>All Status</option>
                                    <option key={'OnlyActive'} value={'OnlyActive'}>
                                        Ongoing
                                    </option>
                                    <option key={'OnlyInactive'} value={'OnlyInactive'}>
                                        Completed
                                    </option>
                                </select>
                                {(selectedUserId !== 'All Employees' || selectedStatus !== 'All Status') && (
                                    <button
                                        onClick={() => handleFilterChange('All Employees', 'All Status')}
                                        className="px-3 py-2 text-sm bg-blue-300 hover:bg-blue-400 rounded-lg transition-colors flex items-center space-x-2 "
                                    >
                                        {/* className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                                    > */}
                                        <X className="h-4 w-4" />
                                        <span>Clear Filters</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-blue-500">
                                <tr>
                                    {timeLogColumns.map(
                                        (column, index) =>
                                            column.active && (
                                                <th
                                                    key={index}
                                                    className="px-6 py-3 text-left text-sm font-medium tracking-wider"
                                                >
                                                    <button className="flex items-center space-x-1 hover:text-gray-700 transition-colors group">
                                                        <span className="uppercase">{column.text}</span>
                                                        {column.sortable && (
                                                            <ArrowUpDown className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                        )}
                                                    </button>
                                                </th>
                                            ),
                                    )}
                                    <th className="px-6 py-3 text-left text-sm uppercase text-white font-medium tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>

                            <tbody className="bg-white divide-y divide-gray-200">
                                {timeLogs.map((timeLog) => (
                                    <tr key={timeLog.id} className="hover:bg-gray-50 transition-colors">
                                        {isColumnActive('user') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center space-x-3">
                                                    <UserIcon className="h-4 w-4 text-gray-400" />
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {timeLog.user?.firstName} {timeLog.user?.lastName}
                                                    </span>
                                                </div>
                                            </td>
                                        )}
                                        {isColumnActive('RO') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {timeLog.repairorder ? (
                                                    <div className="flex items-center space-x-2">
                                                        <Wrench className="h-3 w-3 text-blue-500" />
                                                        <span className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer">
                                                            #{timeLog.repairorder.repairOrderNumber}{' '}
                                                            {timeLog.repairorder.title}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-gray-400 italic">
                                                        No repair order
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                        {isColumnActive('clockInTime') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center space-x-2">
                                                    <Play className="h-3 w-3 text-green-500" />
                                                    <span className="text-sm text-gray-900">
                                                        {moment(timeLog.clockInTime).format(dateFormats.USA_DATETIME)}
                                                    </span>
                                                </div>
                                            </td>
                                        )}
                                        {isColumnActive('clockOutTime') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {timeLog.clockOutTime ? (
                                                    <div className="flex items-center space-x-2">
                                                        <Square className="h-3 w-3 text-red-500" />
                                                        <span className="text-sm text-gray-900">
                                                            {moment(timeLog.clockOutTime).format(
                                                                dateFormats.USA_DATETIME,
                                                            )}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <div className="w-1.5 h-1.5 bg-green-600 rounded-full mr-1.5 animate-pulse"></div>
                                                        Ongoing
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                        {isColumnActive('duration') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {timeLog.clockOutTime && timeLog.clockInTime && (
                                                    <div className="flex items-center space-x-2">
                                                        <Timer className="h-3 w-3 text-purple-500" />
                                                        <span className="text-sm font-medium text-gray-900">
                                                            {getDuration(timeLog)}
                                                        </span>
                                                    </div>
                                                )}
                                            </td>
                                        )}
                                        {isColumnActive('description') && (
                                            <td className="px-6 py-4">
                                                <span className="text-sm text-gray-900 max-w-xs truncate block">
                                                    {timeLog?.description || (
                                                        <span className="italic text-gray-400">No description</span>
                                                    )}
                                                </span>
                                            </td>
                                        )}

                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center space-x-2">
                                                {timeLog.clockOutTime && (
                                                    <>
                                                        <button
                                                            onClick={() => handleEdit(timeLog)}
                                                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                            title="Edit time log"
                                                        >
                                                            <Edit3 className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(timeLog)}
                                                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                            title="Delete time log"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Empty State */}
                    {(!timeLogs || timeLogs.length === 0) && !isLoading && (
                        <div className="py-12 text-center">
                            <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No time logs found</h3>
                            <p className="text-gray-500">
                                Time logs will appear here as employees clock in and out of work sessions.
                            </p>
                        </div>
                    )}
                </div>
            </div>{' '}
            {/* Modals */}
            {isEditTimeLogModelOpen && editableTimeLog && (
                <TimeLogModal
                    timeLog={editableTimeLog}
                    activeRepairOrders={activeRepairOrders}
                    handleClose={() => {
                        setEditableTimeLog(null);
                        setIsEditTimeLogModelOpen(false);
                        fetchTimeLogs(selectedUserId, selectedStatus);
                    }}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    currentUser: state.user.currentUser,
});

export default connect(mapStateToProps)(TimeLogsPage);

const timeLogColumns: TableColumn[] = [
    {
        name: 'user',
        active: true,
        text: 'User',
        sortable: false,
    },
    {
        name: 'RO',
        active: true,
        text: 'Repair Order',
        sortable: false,
    },
    {
        name: 'clockInTime',
        active: true,
        text: 'Start Time',
        sortable: false,
    },
    {
        name: 'clockOutTime',
        active: true,
        text: 'End Time',
        sortable: false,
    },
    {
        name: 'duration',
        active: true,
        text: 'Duration',
        sortable: false,
    },
    {
        name: 'description',
        active: true,
        text: 'Note',
        sortable: false,
    },
];
